import {
    useFormContext,
    useFieldArray,
    FieldValues,
    Path,
    ArrayPath,
} from 'react-hook-form';
import { Flex, Button, IconButton, Box, Card, Badge } from '@radix-ui/themes';
import { ArrayInputConfig } from '../../types';
import { FieldWrapper } from '../FieldWrapper';
import { FieldRenderer } from '../FieldRenderer';
import { useTranslation } from 'react-i18next';

export const ArrayField = <TFormValues extends FieldValues>({
    config,
}: {
    config: ArrayInputConfig<TFormValues>;
}) => {
    const { control } = useFormContext<TFormValues>();
    const { t } = useTranslation('dynamicForm');

    const arrayPath = config.name as ArrayPath<TFormValues>;

    const { fields, append, remove } = useFieldArray({
        control,
        name: arrayPath,
    });

    if (!config.fields) return null;

    return (
        <FieldWrapper config={config}>
            <Flex direction='column' gap='3'>
                {fields.map((fieldItem, index) => (
                    <Card
                        key={fieldItem.id}
                        size='1'
                        style={{ backgroundColor: 'var(--gray-2)' }}
                    >
                        <Flex direction='column' gap='2'>
                            <Flex justify='start'>
                                <Badge
                                    size='1'
                                    color='gray'
                                    variant='surface'
                                    radius='full'
                                >
                                    #{index + 1}
                                </Badge>
                            </Flex>

                            <Flex align='end' gap='3' width='100%'>
                                <Flex
                                    gap='3'
                                    style={{ flexGrow: 1 }}
                                    wrap='wrap'
                                >
                                    {config.fields.map((subField) => {
                                        const nestedName = `${config.name}.${index}.${String(subField.name)}`;
                                        return (
                                            <Box
                                                key={subField.name as string}
                                                style={{ flex: '1 1 200px' }}
                                            >
                                                <FieldRenderer
                                                    config={{
                                                        ...subField,
                                                        name: nestedName as Path<TFormValues>,
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Flex>

                                <Box mb='3'>
                                    <IconButton
                                        type='button'
                                        color='red'
                                        variant='soft'
                                        onClick={() => remove(index)}
                                    >
                                        ✕
                                    </IconButton>
                                </Box>
                            </Flex>
                        </Flex>
                    </Card>
                ))}

                <Flex justify='start'>
                    <Button
                        type='button'
                        variant='outline'
                        onClick={() =>
                            append({} as Parameters<typeof append>[0])
                        }
                    >
                        + {config.buttonLabel || t('buttons.add_item')}
                    </Button>
                </Flex>
            </Flex>
        </FieldWrapper>
    );
};
